"use client";

import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import Api from "../../api/Api";
import Breadcrumbs from "../../components/Common/Breadcrumb";

export default function CampaignMedia() {
  const token = useSelector((state) => state.login.token);
  const [mediaItems, setMediaItems] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const DEFAULT_IMAGE_DURATION = 5;
  const [formData, setFormData] = useState({
    campaign_id: "",
    media_type: "image", // 'image' or 'video'
    duration: DEFAULT_IMAGE_DURATION, // Default 5 seconds for images
    order: 1,
    is_active: true,
  });
  const [uploading, setUploading] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);

  useEffect(() => {
    fetchCampaigns();
    fetchMedia();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setCampaignsLoading(true);
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.get("/admin/campaigns");
      if (response.data.success) {
        setCampaigns(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      toast.error("Failed to fetch campaigns");
    } finally {
      setCampaignsLoading(false);
    }
  };

  const fetchMedia = async () => {
    try {
      setLoading(true);
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.get("/admin/campaign-media");
      if (response.data.success) {
        setMediaItems(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching media:", error);
      toast.error("Failed to fetch campaign media");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
      duration:
        name === "media_type" && value === "image"
          ? DEFAULT_IMAGE_DURATION
          : formData.duration,
    });
  };

  const updateVideoDurationFromFile = (file) => {
    if (!file) return;
    try {
      const objectUrl = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(objectUrl);
        const detectedDuration = Math.ceil(video.duration || 0);
        if (detectedDuration) {
          setFormData((prev) => ({
            ...prev,
            duration: detectedDuration,
          }));
        }
      };
      video.onerror = () => {
        URL.revokeObjectURL(objectUrl);
      };
      video.src = objectUrl;
    } catch (error) {
      console.error("Failed to read video duration", error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (formData.media_type === "image") {
        if (!file.type.startsWith("image/")) {
          toast.error("Please select a valid image file");
          return;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error("Image size should be less than 10MB");
          return;
        }
      } else if (formData.media_type === "video") {
        if (!file.type.startsWith("video/")) {
          toast.error("Please select a valid video file");
          return;
        }
        if (file.size > 100 * 1024 * 1024) {
          toast.error("Video size should be less than 100MB");
          return;
        }
        updateVideoDurationFromFile(file);
      }
      setMediaFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.campaign_id) {
      toast.error("Please select a campaign");
      return;
    }
    setUploading(true);

    try {
      const submitData = new FormData();
      submitData.append("campaign_id", formData.campaign_id);
      submitData.append("media_type", formData.media_type);
      submitData.append("duration", formData.duration);
      submitData.append("order", formData.order);
      submitData.append("is_active", formData.is_active ? 1 : 0);

      if (mediaFile) {
        submitData.append("media", mediaFile);
      } else {
        toast.error("Please upload a media file");
        setUploading(false);
        return;
      }

      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.post("/admin/campaign-media", submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast.success("Campaign media added successfully");
        setModal(false);
        resetForm();
        fetchMedia();
      }
    } catch (error) {
      console.error("Error adding media:", error);
      toast.error(
        error.response?.data?.message || "Failed to add campaign media"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (media) => {
    setSelectedMedia(media);
    setFormData({
      campaign_id: media.campaign_id || "",
      media_type: media.media_type || "image",
      duration: media.duration || 5,
      order: media.order || 1,
      is_active: media.is_active,
    });
    setEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const submitData = new FormData();
      submitData.append("campaign_id", formData.campaign_id);
      submitData.append("media_type", formData.media_type);
      submitData.append("duration", formData.duration);
      submitData.append("order", formData.order);
      submitData.append("is_active", formData.is_active ? 1 : 0);
      submitData.append("_method", "PUT");

      if (mediaFile) {
        submitData.append("media", mediaFile);
      }

      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.post(
        `/admin/campaign-media/${selectedMedia.id}`,
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        toast.success("Campaign media updated successfully");
        setEditModal(false);
        resetForm();
        fetchMedia();
      }
    } catch (error) {
      console.error("Error updating media:", error);
      toast.error(
        error.response?.data?.message || "Failed to update campaign media"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.delete(
        `/admin/campaign-media/${selectedMedia.id}`
      );
      if (response.data.success) {
        toast.success("Campaign media deleted successfully");
        setDeleteModal(false);
        setSelectedMedia(null);
        fetchMedia();
      }
    } catch (error) {
      console.error("Error deleting media:", error);
      toast.error("Failed to delete campaign media");
    }
  };

  const toggleStatus = async (media) => {
    try {
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.put(
        `/admin/campaign-media/${media.id}/status`,
        {
          is_active: !media.is_active,
        }
      );
      if (response.data.success) {
        toast.success(
          `Media ${!media.is_active ? "activated" : "deactivated"} successfully`
        );
        fetchMedia();
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update media status");
    }
  };

  const resetForm = () => {
    setFormData({
      campaign_id: "",
      media_type: "image",
      duration: DEFAULT_IMAGE_DURATION,
      order: 1,
      is_active: true,
    });
    setMediaFile(null);
    setSelectedMedia(null);
  };

  const closeModal = () => {
    setModal(false);
    resetForm();
  };

  const closeEditModal = () => {
    setEditModal(false);
    resetForm();
  };

  const getCampaignName = (campaignId) => {
    const campaign = campaigns.find((c) => c.id === campaignId);
    return campaign ? campaign.title : `Campaign #${campaignId}`;
  };

  if (loading || campaignsLoading) {
    return (
      <Container fluid>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "400px" }}
        >
          <Spinner size="lg" color="primary" />
        </div>
      </Container>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Campaign Media" breadcrumbItem="Campaign Media" />
          <Row>
            <Col>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="card-title mb-0">Campaign Media</h4>
                    <p className="text-muted mb-0">
                      Manage images and videos displayed on influencer home
                      screen
                    </p>
                  </div>
                  <Button
                    color="primary"
                    onClick={() => setModal(true)}
                    className="btn-sm"
                  >
                    <i className="ri-add-line me-1"></i>
                    Add New Media
                  </Button>
                </CardHeader>
                <CardBody>
                  {mediaItems.length === 0 ? (
                    <Alert color="info" className="text-center">
                      <i className="ri-information-line me-2"></i>
                      No campaign media found. Add your first media item to get
                      started.
                    </Alert>
                  ) : (
                    <Row>
                      {mediaItems.map((media) => (
                        <Col lg={4} md={6} key={media.id} className="mb-4">
                          <Card className="h-100">
                            <div className="position-relative">
                              {media.media_type === "video" ? (
                                <video
                                  src={media.media_url}
                                  className="card-img-top"
                                  style={{
                                    height: "200px",
                                    objectFit: "cover",
                                  }}
                                  muted
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = "/placeholder.svg";
                                  }}
                                />
                              ) : (
                                <img
                                  src={media.media_url || "/placeholder.svg"}
                                  alt="Campaign media"
                                  className="card-img-top"
                                  style={{
                                    height: "200px",
                                    objectFit: "cover",
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = "/placeholder.svg";
                                  }}
                                />
                              )}
                              <div className="position-absolute top-0 end-0 p-2">
                                <Badge
                                  color={
                                    media.is_active ? "success" : "secondary"
                                  }
                                  className="fs-12"
                                >
                                  {media.is_active ? "Active" : "Inactive"}
                                </Badge>
                              </div>
                              <div className="position-absolute top-0 start-0 p-2">
                                <Badge
                                  color={
                                    media.media_type === "video"
                                      ? "danger"
                                      : "info"
                                  }
                                  className="fs-12"
                                >
                                  {media.media_type === "video" ? (
                                    <i className="ri-video-line me-1"></i>
                                  ) : (
                                    <i className="ri-image-line me-1"></i>
                                  )}
                                  {media.media_type === "video"
                                    ? "Video"
                                    : "Image"}
                                </Badge>
                              </div>
                            </div>
                            <CardBody>
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="card-title mb-1">
                                  {getCampaignName(media.campaign_id)}
                                </h6>
                                <Badge color="info" className="fs-12">
                                  Order: {media.order}
                                </Badge>
                              </div>
                              <p className="text-muted small mb-2">
                                Duration: {media.duration}s
                              </p>
                              <div className="d-flex gap-2">
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => handleEdit(media)}
                                >
                                  <i className="ri-edit-line"></i>
                                </Button>
                                <Button
                                  color={
                                    media.is_active ? "warning" : "success"
                                  }
                                  size="sm"
                                  onClick={() => toggleStatus(media)}
                                >
                                  <i
                                    className={
                                      media.is_active
                                        ? "ri-pause-circle-line"
                                        : "ri-play-circle-line"
                                    }
                                  ></i>
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedMedia(media);
                                    setDeleteModal(true);
                                  }}
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </Button>
                              </div>
                            </CardBody>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Add Media Modal */}
          <Modal isOpen={modal} toggle={closeModal} size="lg">
            <Form onSubmit={handleSubmit}>
              <ModalHeader toggle={closeModal}>
                Add New Campaign Media
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <Label for="campaign_id">Campaign *</Label>
                  <Input
                    type="select"
                    id="campaign_id"
                    name="campaign_id"
                    value={formData.campaign_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a campaign</option>
                    {campaigns.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.title} (ID: {campaign.id})
                      </option>
                    ))}
                  </Input>
                </FormGroup>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="media_type">Media Type *</Label>
                      <Input
                        type="select"
                        id="media_type"
                        name="media_type"
                        value={formData.media_type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="image">Image</option>
                        <option value="video">Video</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    {formData.media_type === "image" ? (
                      <FormGroup>
                        <Label for="duration">
                          Duration (seconds) *
                          <small className="text-muted ms-1">
                            (Default: 5s)
                          </small>
                        </Label>
                        <Input
                          type="number"
                          id="duration"
                          name="duration"
                          value={formData.duration}
                          onChange={handleInputChange}
                          min="1"
                          required
                        />
                        <small className="text-muted">
                          Images will display for this duration
                        </small>
                      </FormGroup>
                    ) : (
                      <FormGroup>
                        <Label>Duration</Label>
                        <div>
                          <small className="text-muted">
                            Video duration is detected automatically
                            {formData.duration
                              ? ` (${formData.duration}s)`
                              : ""}
                          </small>
                        </div>
                      </FormGroup>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="order">Display Order</Label>
                      <Input
                        type="number"
                        id="order"
                        name="order"
                        value={formData.order}
                        onChange={handleInputChange}
                        min="1"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className="mt-4">
                      <Input
                        type="checkbox"
                        id="is_active"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                      <Label check for="is_active">
                        Active
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label for="media">
                    Upload {formData.media_type === "video" ? "Video" : "Image"}
                  </Label>
                  <Input
                    type="file"
                    id="media"
                    accept={
                      formData.media_type === "video" ? "video/*" : "image/*"
                    }
                    onChange={handleFileChange}
                  />
                  <small className="text-muted">
                    {formData.media_type === "video"
                      ? `Supported formats: MP4, MOV, AVI. Max size: 100MB.${
                          formData.duration
                            ? ` Detected duration: ${formData.duration}s.`
                            : ""
                        }`
                      : "Supported formats: JPG, PNG, GIF. Max size: 10MB"}
                  </small>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={uploading}>
                  {uploading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Adding...
                    </>
                  ) : (
                    "Add Media"
                  )}
                </Button>
              </ModalFooter>
            </Form>
          </Modal>

          {/* Edit Media Modal */}
          <Modal isOpen={editModal} toggle={closeEditModal} size="lg">
            <Form onSubmit={handleUpdate}>
              <ModalHeader toggle={closeEditModal}>
                Edit Campaign Media
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <Label for="edit_campaign_id">Campaign *</Label>
                  <Input
                    type="select"
                    id="edit_campaign_id"
                    name="campaign_id"
                    value={formData.campaign_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a campaign</option>
                    {campaigns.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.title} (ID: {campaign.id})
                      </option>
                    ))}
                  </Input>
                </FormGroup>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="edit_media_type">Media Type *</Label>
                      <Input
                        type="select"
                        id="edit_media_type"
                        name="media_type"
                        value={formData.media_type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="image">Image</option>
                        <option value="video">Video</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    {formData.media_type === "image" ? (
                      <FormGroup>
                        <Label for="edit_duration">Duration (seconds) *</Label>
                        <Input
                          type="number"
                          id="edit_duration"
                          name="duration"
                          value={formData.duration}
                          onChange={handleInputChange}
                          min="1"
                          required
                        />
                      </FormGroup>
                    ) : (
                      <FormGroup>
                        <Label>Duration</Label>
                        <div>
                          <small className="text-muted">
                            Video duration is detected automatically
                            {formData.duration
                              ? ` (${formData.duration}s)`
                              : ""}
                          </small>
                        </div>
                      </FormGroup>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="edit_order">Display Order</Label>
                      <Input
                        type="number"
                        id="edit_order"
                        name="order"
                        value={formData.order}
                        onChange={handleInputChange}
                        min="1"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className="mt-4">
                      <Input
                        type="checkbox"
                        id="edit_is_active"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                      <Label check for="edit_is_active">
                        Active
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
                {selectedMedia && (
                  <FormGroup>
                    <Label>Current Media</Label>
                    <div>
                      {selectedMedia.media_type === "video" ? (
                        <video
                          src={selectedMedia.media_url}
                          style={{
                            maxWidth: "200px",
                            height: "auto",
                            borderRadius: "4px",
                          }}
                          controls
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/placeholder.svg";
                          }}
                        />
                      ) : (
                        <img
                          src={selectedMedia.media_url || "/placeholder.svg"}
                          alt="Current"
                          style={{
                            maxWidth: "200px",
                            height: "auto",
                            borderRadius: "4px",
                          }}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/placeholder.svg";
                          }}
                        />
                      )}
                    </div>
                  </FormGroup>
                )}
                <FormGroup>
                  <Label for="edit_media">
                    Upload New{" "}
                    {formData.media_type === "video" ? "Video" : "Image"}
                  </Label>
                  <Input
                    type="file"
                    id="edit_media"
                    accept={
                      formData.media_type === "video" ? "video/*" : "image/*"
                    }
                    onChange={handleFileChange}
                  />
                  <small className="text-muted">
                    Leave empty to keep current media
                  </small>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={closeEditModal}>
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={uploading}>
                  {uploading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Updating...
                    </>
                  ) : (
                    "Update Media"
                  )}
                </Button>
              </ModalFooter>
            </Form>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal
            isOpen={deleteModal}
            toggle={() => setDeleteModal(false)}
            centered
          >
            <ModalHeader toggle={() => setDeleteModal(false)}>
              Confirm Delete
            </ModalHeader>
            <ModalBody>
              <div className="text-center">
                <i
                  className="ri-delete-bin-line text-danger"
                  style={{ fontSize: "48px" }}
                ></i>
                <h5 className="mt-3">Are you sure?</h5>
                <p className="text-muted">
                  Do you want to delete this campaign media? This action cannot
                  be undone.
                </p>
                {selectedMedia && (
                  <div className="mt-3">
                    {selectedMedia.media_type === "video" ? (
                      <video
                        src={selectedMedia.media_url}
                        style={{
                          maxWidth: "150px",
                          height: "auto",
                          borderRadius: "4px",
                        }}
                        muted
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = "/placeholder.svg";
                        }}
                      />
                    ) : (
                      <img
                        src={selectedMedia.media_url || "/placeholder.svg"}
                        alt="To delete"
                        style={{
                          maxWidth: "150px",
                          height: "auto",
                          borderRadius: "4px",
                        }}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = "/placeholder.svg";
                        }}
                      />
                    )}
                    <p className="mt-2 fw-medium">
                      {getCampaignName(selectedMedia.campaign_id)}
                    </p>
                  </div>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setDeleteModal(false)}>
                Cancel
              </Button>
              <Button color="danger" onClick={handleDelete}>
                <i className="ri-delete-bin-line me-1"></i>
                Delete
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
}
